import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Edit,
  Users,
  Crown,
  X,
  Save,
  Loader,
  RotateCcw,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  UserPlus,
  Check,
} from 'lucide-react';
import {
  listAllUsers,
  updateUserById,
  resetUserPassword,
  deleteUser,
  createUser,
} from '../../services/userManagementService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [totalScanned, setTotalScanned] = useState(0);
  const usersPerPage = 10;
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isAdmin: false,
    emailGameInvitations: false,
    emailGameResults: false,
    phoneGameInvitations: false,
    phoneGameResults: false,
  });
  const [saving, setSaving] = useState(false);

  // Password reset state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Delete user states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState('');
  const [deleteUserName, setDeleteUserName] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);

  // Create new user states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    isAdmin: false,
    emailNotifications: false,
    phoneNotifications: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [useRandomPassword, setUseRandomPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [createUserError, setCreateUserError] = useState('');

  const loadUsers = useCallback(
    async (append = false, pageKey = null) => {
      setLoading(true);
      setError('');
      try {
        const result = await listAllUsers(searchTerm, usersPerPage, pageKey);
        if (result.success) {
          if (append) {
            setUsers((prev) => [...prev, ...result.users]);
          } else {
            setUsers(result.users);
            setCurrentPage(1);
          }
          setHasMore(result.hasMore);
          setLastEvaluatedKey(result.lastEvaluatedKey);
          setTotalScanned(result.totalScanned || 0);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, usersPerPage]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setUsers([]);
    setCurrentPage(1);
    setLastEvaluatedKey(null);
    setHasMore(true);
    loadUsers();
  };

  const handleLoadMore = () => {
    if (hasMore && !loading && lastEvaluatedKey) {
      setCurrentPage((prev) => prev + 1);
      loadUsers(true, lastEvaluatedKey);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      isAdmin: user.isAdmin || false,
      emailGameInvitations: user.emailGameInvitations || false,
      emailGameResults: user.emailGameResults || false,
      phoneGameInvitations: user.phoneGameInvitations || false,
      phoneGameResults: user.phoneGameResults || false,
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isAdmin: false,
      emailGameInvitations: false,
      emailGameResults: false,
      phoneGameInvitations: false,
      phoneGameResults: false,
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    setSaving(true);
    try {
      const result = await updateUserById(editingUser.userId, editForm);
      if (result.success) {
        setUsers((prev) =>
          prev.map((u) => (u.userId === editingUser.userId ? result.user : u))
        );
        closeEditModal();
        setError('');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPasswordClick = (user) => {
    setResetUserId(user.userId);
    setResetUserName(`${user.firstName} ${user.lastName}`);
    setShowResetConfirm(true);
    setTempPassword('');
    setShowTempPassword(false);
    setCopied(false);
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetUserId) return;

    setResettingPassword(true);
    try {
      const result = await resetUserPassword(resetUserId);
      if (result.success) {
        setTempPassword(result.tempPassword);
        setShowTempPassword(true);
        setShowResetConfirm(false);
        setError('');
        setUsers((prev) =>
          prev.map((u) => (u.userId === resetUserId ? result.user : u))
        );
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleResetPasswordCancel = () => {
    setShowResetConfirm(false);
    setResetUserId(null);
    setResetUserName('');
    setTempPassword('');
    setShowTempPassword(false);
    setCopied(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const closeTempPasswordModal = () => {
    setShowTempPassword(false);
    setTempPassword('');
    setPasswordVisible(false);
    setCopied(false);
  };

  const handleDeleteUserClick = (user) => {
    setDeleteUserId(user.userId);
    setDeleteUserName(
      `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    );
    setShowDeleteConfirm(true);
  };

  const handleDeleteUserConfirm = async () => {
    if (!deleteUserId) return;

    setDeletingUser(true);
    try {
      const result = await deleteUser(deleteUserId);

      if (result.success) {
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.userId !== deleteUserId)
        );
        let message = result.message;
        if (result.warnings && result.warnings.length > 0) {
          message += '\n\nWarnings:\n' + result.warnings.join('\n');
        }
        setSuccessMessage(message);
        setError('');
        handleDeleteUserCancel();
      } else {
        setError('Failed to delete user: ' + result.error);
        setSuccessMessage('');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user: ' + error.message);
      setSuccessMessage('');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleDeleteUserCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteUserId('');
    setDeleteUserName('');
  };

  const handleCreateUserClick = () => {
    setCreateUserForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      isAdmin: false,
      emailNotifications: false,
      phoneNotifications: false,
    });
    setShowCreateUser(true);
  };

  const handleCreateUserCancel = () => {
    setShowCreateUser(false);
    setCreateUserForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      isAdmin: false,
      emailNotifications: false,
      phoneNotifications: false,
    });
    setUseRandomPassword(false);
    setGeneratedPassword('');
    setCreateUserError('');
  };

  const generateRandomPassword = () => {
    const chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    for (let i = 4; i < 12; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    password = password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setGeneratedPassword(newPassword);
    setCreateUserForm({
      ...createUserForm,
      password: newPassword,
      confirmPassword: newPassword,
    });
    setUseRandomPassword(true);
  };

  const handlePasswordChange = (field, value) => {
    setCreateUserForm({ ...createUserForm, [field]: value });
    if (
      useRandomPassword &&
      (field === 'password' || field === 'confirmPassword')
    ) {
      setUseRandomPassword(false);
      setGeneratedPassword('');
    }
  };

  const handleCreateUserConfirm = async () => {
    const {
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
      isAdmin,
      emailNotifications,
      phoneNotifications,
    } = createUserForm;

    // Clear previous errors
    setCreateUserError('');

    if (!firstName || !lastName || !password) {
      setCreateUserError('First name, last name, and password are required');
      return;
    }

    if (!email && !phone) {
      setCreateUserError('Either email or phone number is required');
      return;
    }

    if (password !== confirmPassword) {
      setCreateUserError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setCreateUserError('Password must be at least 6 characters long');
      return;
    }

    setCreatingUser(true);
    try {
      const userData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        password,
        isAdmin,
        // Set notification preferences
        emailGameInvitations: emailNotifications,
        emailGameResults: emailNotifications,
        phoneGameInvitations: phoneNotifications,
        phoneGameResults: phoneNotifications,
      };

      const result = await createUser(userData);

      if (result.success) {
        setUsers((prevUsers) => [result.user, ...prevUsers]);
        handleCreateUserCancel();
      } else {
        if (result.statusCode === 409) {
          setCreateUserError(
            'A user with this email address or phone number already exists.'
          );
        } else {
          setCreateUserError('Failed to create user: ' + result.error);
        }
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setCreateUserError('Failed to create user: ' + error.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
      </div>

      {/* Search and Create User */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-4 flex-1">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </form>

        {/* Create New User Button */}
        <button
          onClick={handleCreateUserClick}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Create New User
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Success Display */}
      {successMessage && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded whitespace-pre-line">
          {successMessage}
        </div>
      )}

      {/* Users Table - Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                User
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                Email
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                Role
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                Created
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 font-medium text-sm">
                          {(user.firstName || '')[0]}
                          {(user.lastName || '')[0]}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-900">{user.email}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {user.phone || 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {user.isAdmin && (
                        <Crown className="w-4 h-4 text-yellow-500" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          user.isAdmin ? 'text-yellow-700' : 'text-gray-700'
                        }`}
                      >
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-900">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50 transition-colors"
                        title="Edit user"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPasswordClick(user)}
                        className="text-yellow-600 hover:text-yellow-800 p-1 rounded-md hover:bg-yellow-50 transition-colors"
                        title="Reset password"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUserClick(user)}
                        className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Users List - Mobile View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="py-8 text-center text-gray-500">
            <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No users found</div>
        ) : (
          users.map((user) => (
            <div
              key={user.userId}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-medium text-sm">
                      {(user.firstName || '')[0]}
                      {(user.lastName || '')[0]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {user.isAdmin && (
                        <Crown className="w-4 h-4 text-yellow-500" />
                      )}
                      <span
                        className={`text-xs font-medium ${
                          user.isAdmin ? 'text-yellow-700' : 'text-gray-600'
                        }`}
                      >
                        {user.isAdmin ? 'Admin' : 'User'}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => openEditModal(user)}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-md hover:bg-blue-50 transition-colors"
                  title="View/Edit user"
                >
                  <Edit className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {users.length > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing {users.length} user{users.length !== 1 ? 's' : ''}
            {totalScanned > 0 && ` (scanned ${totalScanned} records)`}
          </div>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                'Load More Users'
              )}
            </button>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveUser} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={editForm.isAdmin}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        isAdmin: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="isAdmin"
                    className="ml-2 text-sm font-medium text-gray-700 flex items-center gap-1"
                  >
                    <Crown className="w-4 h-4 text-yellow-500" />
                    Admin User
                  </label>
                </div>

                {/* Notification Preferences */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Notification Preferences
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editEmailGameInvitations"
                        checked={editForm.emailGameInvitations}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            emailGameInvitations: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="editEmailGameInvitations"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Email game invitations
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editEmailGameResults"
                        checked={editForm.emailGameResults}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            emailGameResults: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="editEmailGameResults"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Email game results
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editPhoneGameInvitations"
                        checked={editForm.phoneGameInvitations}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            phoneGameInvitations: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="editPhoneGameInvitations"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        SMS game invitations
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="editPhoneGameResults"
                        checked={editForm.phoneGameResults}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            phoneGameResults: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="editPhoneGameResults"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        SMS game results
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              {/* Mobile Action Buttons */}
              <div className="md:hidden flex gap-2 mb-6">
                <button
                  type="button"
                  onClick={() => {
                    closeEditModal();
                    handleResetPasswordClick(editingUser);
                  }}
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeEditModal();
                    handleDeleteUserClick(editingUser);
                  }}
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete User
                </button>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={saving}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-red-900">
                Reset Password
              </h3>
              <button
                onClick={handleResetPasswordCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Reset Password for:
                  </p>
                  <p className="text-sm text-gray-600">{resetUserName}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will generate a new temporary
                  password. The user will need to change it on their next login.
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to reset this user's password? They will
                receive a temporary password that must be changed on next login.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleResetPasswordCancel}
                  disabled={resettingPassword}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPasswordConfirm}
                  disabled={resettingPassword}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingPassword ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Temporary Password Display Modal */}
      {showTempPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-green-900">
                Password Reset Complete
              </h3>
              <button
                onClick={closeTempPasswordModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    New Temporary Password
                  </p>
                  <p className="text-sm text-gray-600">For: {resetUserName}</p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-green-800">
                    Temporary Password:
                  </label>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="text-green-600 hover:text-green-800 p-1"
                      title={
                        passwordVisible ? 'Hide password' : 'Show password'
                      }
                    >
                      {passwordVisible ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(tempPassword)}
                      className={`p-1 rounded ${copied ? 'text-white bg-green-600' : 'text-green-600 hover:text-green-800'}`}
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-green-300 rounded px-3 py-2 flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-900">
                    {passwordVisible ? tempPassword : '••••••••••••'}
                  </code>
                  {useRandomPassword && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(tempPassword)}
                      className={`ml-3 px-2 py-1 text-xs rounded flex items-center gap-1 ${copied ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white hover:bg-yellow-700'}`}
                    >
                      {copied ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> Please securely communicate this
                  password to the user. They will be required to change it on
                  their next login.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={closeTempPasswordModal}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-red-900">
                Delete User
              </h3>
              <button
                onClick={handleDeleteUserCancel}
                className="text-gray-400 hover:text-gray-600"
                disabled={deletingUser}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Delete User Account
                  </p>
                  <p className="text-sm text-gray-600">{deleteUserName}</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ This action cannot be undone!
                </p>
                <p className="text-sm text-red-700">
                  This will permanently delete the user account and remove them
                  from all groups. Any games they created or participated in
                  will remain, but their account will be gone forever.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleDeleteUserCancel}
                  disabled={deletingUser}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUserConfirm}
                  disabled={deletingUser}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingUser ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deletingUser ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create New User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-green-900">
                Create New User
              </h3>
              <button
                onClick={handleCreateUserCancel}
                className="text-gray-400 hover:text-gray-600"
                disabled={creatingUser}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Create New Account
                  </p>
                  <p className="text-sm text-gray-600">
                    Create a new user account with login credentials
                  </p>
                </div>
              </div>

              <form className="space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={createUserForm.firstName}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={creatingUser}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={createUserForm.lastName}
                      onChange={(e) =>
                        setCreateUserForm({
                          ...createUserForm,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={creatingUser}
                      required
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={createUserForm.phone}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        phone: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingUser}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    * At least one of email or phone number is required
                  </p>
                </div>

                {/* Password Fields */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      disabled={creatingUser}
                      className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      Generate Random Password
                    </button>
                  </div>
                  <input
                    type="password"
                    value={createUserForm.password}
                    onChange={(e) =>
                      handlePasswordChange('password', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingUser}
                    required
                    minLength={6}
                  />
                  {useRandomPassword && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Secure random password generated
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    value={createUserForm.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange('confirmPassword', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={creatingUser}
                    required
                    minLength={6}
                  />
                </div>

                {/* Show generated password for admin to copy */}
                {useRandomPassword && generatedPassword && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-xs text-yellow-600">!</span>
                      </div>
                      <span className="text-sm font-medium text-yellow-800">
                        Generated Password
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="font-mono text-sm bg-white border rounded p-2 flex-1">
                        {generatedPassword}
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(generatedPassword)}
                        className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                          copied
                            ? 'bg-green-600 text-white'
                            : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        }`}
                        title="Copy password to clipboard"
                      >
                        {copied ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-yellow-700">
                      Save this password! You'll need to share it with the user
                      securely.
                    </p>
                  </div>
                )}

                {/* Admin Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={createUserForm.isAdmin}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        isAdmin: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    disabled={creatingUser}
                  />
                  <label
                    htmlFor="isAdmin"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Admin user (can manage all users and groups)
                  </label>
                </div>

                {/* Notification Preferences */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Notification Preferences
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="emailNotifications"
                        checked={createUserForm.emailNotifications}
                        onChange={(e) =>
                          setCreateUserForm({
                            ...createUserForm,
                            emailNotifications: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={creatingUser}
                      />
                      <label
                        htmlFor="emailNotifications"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Email notifications (game invitations & results)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="phoneNotifications"
                        checked={createUserForm.phoneNotifications}
                        onChange={(e) =>
                          setCreateUserForm({
                            ...createUserForm,
                            phoneNotifications: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={creatingUser}
                      />
                      <label
                        htmlFor="phoneNotifications"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        SMS notifications (game invitations & results)
                      </label>
                    </div>
                  </div>
                </div>
              </form>

              {/* Error Display */}
              {createUserError && (
                <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                  {createUserError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
              <button
                onClick={handleCreateUserCancel}
                disabled={creatingUser}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUserConfirm}
                disabled={creatingUser}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingUser ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
